import { api } from "@/lib/ky";
import { StoreProduct } from "@medusajs/types";
import { getPrismicLocale, Locales } from "@/lib/i18n";
import { getAllByType, getDocument } from "@/lib/prismic";
import { filter } from "@prismicio/client";
import { ProductDocument } from "@/prismicio-types";

export const fetchSingleProduct = async (locale: Locales, uid: string): Promise<StoreProduct | null> => {
  const { product } = await api.get(`products/${locale}/${uid}`).json<{ product: StoreProduct | null }>();

  return product;
};

export const fetchMultipleProducts = async (locale: Locales, uids: string[]): Promise<Record<string, StoreProduct>> => {
  if (!uids || uids.length === 0) {
    console.warn("fetchMultipleProducts called with empty or undefined uids array");
    return {};
  }
  const { products } = await api
    .post(`products/${locale}/batch`, {
      json: { uids },
    })
    .json<{ products: Record<string, StoreProduct> }>();

  return products;
};

export const fetchStorefrontProductAvailability = async (locale: Locales, slug: string): Promise<boolean> => {
  const { available } = await api
    .get(`storefront-products/${locale}/${encodeURIComponent(slug)}/availability`)
    .json<{ available: boolean }>();

  return available;
};

export const fetchPrismicProduct = async (locale: Locales, medusaId: string) => {
  const products = (await getAllByType("product", locale, {
    filters: [filter.at("my.product.medusaId", medusaId)],
  })) as ProductDocument[] | null;

  const product = products?.at(0);

  if (!product || !product.data.medusaId) return null;

  if (locale === "fr") return product;

  const frenchUid = product.alternate_languages.find((alt) => alt.lang === getPrismicLocale("fr"))?.uid || product.uid;

  const frenchProduct = (await getDocument("product", frenchUid, "fr")) as ProductDocument | null;

  if (frenchProduct) {
    return {
      ...product,
      data: {
        ...product.data,
        images: frenchProduct.data.images,
        featuredImage: frenchProduct.data.featuredImage,
        productCollection: frenchProduct.data.productCollection,
        image_full_size_card: frenchProduct.data.image_full_size_card,
      },
    };
  }

  return product;
};

export const fetchMultiplePrismicProduct = async (locale: Locales, uids: string[], params: object = {}) => {
  if (!uids || !uids[0]) return null;

  const products = (await getAllByType("product", locale, {
    filters: [filter.any("my.product.medusaId", uids)],
    ...params,
  })) as ProductDocument[] | null;

  if (locale === "fr") return products;

  const frenchUids =
    products?.map(
      (product) => product.alternate_languages.find((alt) => alt.lang === getPrismicLocale("fr"))?.uid || product.uid
    ) || [];

  const frenchProducts = (await getAllByType("product", "fr", {
    filters: [filter.any("my.product.uid", frenchUids)],
    ...params,
  })) as ProductDocument[] | null;

  if (!frenchProducts || !products) return products || [];

  const result = products
    .map((product) => {
      const frenchEquivalent = frenchProducts?.find(
        (frenchProduct) =>
          frenchProduct.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid === product.uid
      );
      if (frenchEquivalent) {
        return {
          ...product,
          data: {
            ...product.data,
            images: frenchEquivalent.data.images,
            featuredImage: frenchEquivalent.data.featuredImage,
            productCollection: frenchEquivalent.data.productCollection,
            image_full_size_card: frenchEquivalent.data.image_full_size_card,
          },
        };
      }
      return undefined;
    })
    .filter(Boolean) as ProductDocument[];

  return result;
};
